import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { T, fmt, Card, Boton, Aviso, Cargando } from '../lib/ui';
import { mensajeDeError } from './Gastos';

// ════════════════════════════════════════════════════════════════════
// Desviaciones y presupuesto
//
// La pregunta no es cuanto cambio, sino por que. Cada desviacion se
// parte en dos causas que exigen acciones distintas: hacer mas
// operaciones, o que cada una cueste mas.
// ════════════════════════════════════════════════════════════════════

const COLOR_CAUSA = {
  volumen:         { color: T.info,  texto: 'Volumen' },
  precio:          { color: T.bad,   texto: 'Precio' },
  mixta:           { color: T.warn,  texto: 'Mixta' },
  concepto_nuevo:  { color: T.brass, texto: 'Nuevo' },
  concepto_cesado: { color: T.muted, texto: 'Cesó' },
  estable:         { color: T.muted, texto: 'Estable' },
  sin_movimiento:  { color: T.muted, texto: '—' },
};

const ESTADO_PRESU = {
  excedido:        { color: T.bad,   fondo: T.badBg,  texto: 'Excedido' },
  proyecta_exceso: { color: T.warn,  fondo: T.warnBg, texto: 'Proyecta exceso' },
  adelantado:      { color: T.warn,  fondo: T.warnBg, texto: 'Adelantado' },
  en_linea:        { color: T.ok,    fondo: T.okBg,   texto: 'En línea' },
  sin_presupuesto: { color: T.muted, fondo: T.warm,   texto: 'Sin presupuesto' },
};

// ════════════════════════════════════════════════════════════════════
export function VistaDesviaciones({ filtros, moneda }) {
  const [d, setD] = useState(null);
  const [detalle, setDetalle] = useState(null);
  const [fallo, setFallo] = useState('');
  const [soloRelevantes, setSoloRelevantes] = useState(true);

  useEffect(() => {
    setD(null); setFallo(''); setDetalle(null);
    const f = { ...filtros };
    if (!f.ov) delete f.ov;
    api.reporteDesviaciones(f)
      .then(setD)
      .catch((err) => { setFallo(mensajeDeError(err)); setD({}); });
  }, [filtros]);

  if (fallo) return <Aviso tipo="bad">{fallo}</Aviso>;
  if (!d) return <Cargando />;

  const conceptos = soloRelevantes
    ? (d.conceptos || []).filter((c) => c.relevante)
    : (d.conceptos || []);

  const subeTotal = (d.variacionTotal || 0) > 0;

  return (
    <>
      <Card style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 11, color: T.muted, marginBottom: 10 }}>
          {fmt.fecha(d.periodo?.desde)} a {fmt.fecha(d.periodo?.hasta)}
          {' vs '}
          {fmt.fecha(d.periodoAnterior?.desde)} a {fmt.fecha(d.periodoAnterior?.hasta)}
        </div>

        <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 10, color: T.muted, letterSpacing: '.05em' }}>
              PERIODO ANTERIOR
            </div>
            <div style={{ fontSize: 18, fontWeight: 600, color: T.muted }}>
              {fmt.money(d.totalAnterior, moneda)}
            </div>
          </div>

          <div style={{ fontSize: 20, color: T.muted }}>→</div>

          <div>
            <div style={{ fontSize: 10, color: T.muted, letterSpacing: '.05em' }}>
              PERIODO ACTUAL
            </div>
            <div style={{ fontSize: 22, fontWeight: 700, color: T.navy }}>
              {fmt.money(d.totalActual, moneda)}
            </div>
          </div>

          <div style={{
            marginLeft: 'auto', textAlign: 'right',
            color: subeTotal ? T.bad : T.ok,
          }}>
            <div style={{ fontSize: 19, fontWeight: 700 }}>
              {subeTotal ? '+' : ''}{fmt.money(d.variacionTotal, moneda)}
            </div>
            {d.variacionTotalPct != null && (
              <div style={{ fontSize: 12 }}>
                {subeTotal ? '+' : ''}{d.variacionTotalPct}%
              </div>
            )}
          </div>
        </div>
      </Card>

      <div style={{
        display: 'flex', justifyContent: 'space-between',
        alignItems: 'center', marginBottom: 11, gap: 10,
      }}>
        <span style={{ fontSize: 12, color: T.muted }}>
          {d.relevantes} desviación(es) relevante(s)
        </span>
        <button onClick={() => setSoloRelevantes(!soloRelevantes)} style={{
          fontSize: 12, padding: '5px 11px', borderRadius: 14, cursor: 'pointer',
          border: `1px solid ${T.border}`, background: T.paper, color: T.text,
        }}>
          {soloRelevantes ? 'Ver todos' : 'Solo relevantes'}
        </button>
      </div>

      {conceptos.length === 0 ? (
        <Aviso tipo="ok">
          Sin desviaciones que superen a la vez el 25% y los 300 en monto.
          El gasto se mantiene estable respecto al periodo anterior.
        </Aviso>
      ) : (
        <div style={{ display: 'grid', gap: 10 }}>
          {conceptos.map((c) => (
            <TarjetaConcepto
              key={c.cuenta} c={c} moneda={moneda}
              abierto={detalle === c.cuenta}
              onAbrir={() => setDetalle(detalle === c.cuenta ? null : c.cuenta)}
              filtros={filtros}
            />
          ))}
        </div>
      )}

      <div style={{ fontSize: 12, color: T.muted, marginTop: 16, lineHeight: 1.6 }}>
        Una desviación se marca como relevante solo si supera a la vez el 25% y
        los 300 de monto. Un 300% sobre 20 no es noticia, y un 4% sobre 200 mil
        tampoco.
      </div>
    </>
  );
}

function TarjetaConcepto({ c, moneda, abierto, onAbrir, filtros }) {
  const [causa, setCausa] = useState(null);
  const sube = c.variacion > 0;
  const cc = COLOR_CAUSA[c.causa] || COLOR_CAUSA.estable;

  useEffect(() => {
    if (!abierto || causa) return;
    const f = { ...filtros };
    if (!f.ov) delete f.ov;
    api.causaDesviacion(c.cuenta, f).then(setCausa).catch(() => setCausa({}));
  }, [abierto]);   // eslint-disable-line react-hooks/exhaustive-deps

  const totalEfecto = Math.abs(c.efectoVolumen) + Math.abs(c.efectoPrecio);
  const pctVol = totalEfecto > 0 ? (Math.abs(c.efectoVolumen) / totalEfecto) * 100 : 50;

  return (
    <Card style={{ padding: 15, borderLeft: `3px solid ${sube ? T.bad : T.ok}` }}>
      <div onClick={onAbrir} style={{ cursor: 'pointer' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 600 }}>{c.nombre}</div>
            <div style={{ fontSize: 11, color: T.muted, marginTop: 3 }}>
              {fmt.money(c.anterior.total, moneda)} ({c.anterior.cantidad})
              {' → '}
              {fmt.money(c.actual.total, moneda)} ({c.actual.cantidad})
            </div>
          </div>
          <div style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: sube ? T.bad : T.ok }}>
              {sube ? '+' : ''}{fmt.money(c.variacion, moneda)}
            </div>
            {c.variacionPct != null && (
              <div style={{ fontSize: 12, color: sube ? T.bad : T.ok }}>
                {sube ? '+' : ''}{c.variacionPct}%
              </div>
            )}
          </div>
        </div>

        {/* Reparto de la causa */}
        {['volumen', 'precio', 'mixta'].includes(c.causa) && (
          <div style={{ marginTop: 11 }}>
            <div style={{
              display: 'flex', height: 7, borderRadius: 4,
              overflow: 'hidden', background: T.warm,
            }}>
              <div style={{ width: `${pctVol}%`, background: T.info }} />
              <div style={{ width: `${100 - pctVol}%`, background: T.bad }} />
            </div>
            <div style={{
              display: 'flex', justifyContent: 'space-between',
              fontSize: 11, color: T.muted, marginTop: 4,
            }}>
              <span style={{ color: T.info }}>
                Volumen {fmt.money(c.efectoVolumen, moneda)}
              </span>
              <span style={{ color: T.bad }}>
                Precio {fmt.money(c.efectoPrecio, moneda)}
              </span>
            </div>
          </div>
        )}

        <div style={{
          marginTop: 10, display: 'flex', gap: 8,
          alignItems: 'center', flexWrap: 'wrap',
        }}>
          <span style={{
            fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10,
            background: `${cc.color}1A`, color: cc.color, letterSpacing: '.03em',
          }}>
            {cc.texto.toUpperCase()}
          </span>
          <span style={{ fontSize: 12, color: T.text, flex: 1, minWidth: 0 }}>
            {c.lectura}
          </span>
        </div>

        <div style={{ fontSize: 11, color: T.brass, marginTop: 8 }}>
          {abierto ? 'Ocultar detalle' : 'Ver qué lo causó'}
        </div>
      </div>

      {abierto && (
        <div style={{ marginTop: 13, paddingTop: 13, borderTop: `1px solid ${T.border}` }}>
          {!causa ? <Cargando /> : <Causas causa={causa} moneda={moneda} />}
        </div>
      )}
    </Card>
  );
}

function Causas({ causa, moneda }) {
  const hayAlgo = (causa.proveedoresNuevos?.length || 0)
    + (causa.proveedoresEncarecidos?.length || 0)
    + (causa.cambioPersonas?.length || 0) > 0;

  if (!hayAlgo) {
    return (
      <div style={{ fontSize: 12, color: T.muted }}>
        No se identificaron proveedores nuevos ni cambios de precio. Revisa los
        movimientos individuales.
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      {causa.proveedoresEncarecidos?.length > 0 && (
        <Bloque titulo="Proveedores que subieron precio">
          {causa.proveedoresEncarecidos.map((p, i) => (
            <Fila key={i}
                  izquierda={p.razon}
                  derecha={`+${p.subidaPct}%`}
                  color={T.bad}
                  detalle={`${fmt.money(p.ticketAnterior, moneda)} → ${fmt.money(p.ticketActual, moneda)} por operación`} />
          ))}
        </Bloque>
      )}

      {causa.proveedoresNuevos?.length > 0 && (
        <Bloque titulo="Proveedores que antes no aparecían">
          {causa.proveedoresNuevos.map((p, i) => (
            <Fila key={i}
                  izquierda={p.razon || p.ruc}
                  derecha={fmt.money(p.total, moneda)}
                  detalle={`${p.cantidad} comprobante(s)`} />
          ))}
        </Bloque>
      )}

      {causa.cambioPersonas?.length > 0 && (
        <Bloque titulo="Quién gastó más o menos">
          {causa.cambioPersonas.map((p, i) => (
            <Fila key={i}
                  izquierda={p.nombre}
                  derecha={`${p.variacion > 0 ? '+' : ''}${fmt.money(p.variacion, moneda)}`}
                  color={p.variacion > 0 ? T.bad : T.ok}
                  detalle={`${p.cantidadAnterior} → ${p.cantidadActual} gasto(s)`} />
          ))}
        </Bloque>
      )}

      {causa.mayoresGastos?.length > 0 && (
        <Bloque titulo="Movimientos más grandes del periodo">
          {causa.mayoresGastos.map((g) => (
            <Fila key={g.id}
                  izquierda={g.concepto || g.proveedor || g.id}
                  derecha={fmt.money(g.total, moneda)}
                  detalle={`${fmt.fecha(g.fecha)}${g.persona ? ` · ${g.persona}` : ''}${g.ov ? ` · ${g.ov}` : ''}`} />
          ))}
        </Bloque>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
export function VistaPresupuesto({ filtros, moneda, usuario }) {
  const [d, setD] = useState(null);
  const [editando, setEditando] = useState(null);
  const [aviso, setAviso] = useState(null);
  const esDirector = usuario?.rol === 'director';

  const cargar = () => {
    const f = { ...filtros };
    if (!f.ov) delete f.ov;
    api.reportePresupuesto(f).then(setD).catch(() => setD({}));
  };

  useEffect(() => { setD(null); cargar(); }, [filtros]);  // eslint-disable-line

  if (!d) return <Cargando />;
  const proyectos = d.proyectos || [];

  return (
    <>
      {aviso && <Aviso tipo={aviso.tipo}>{aviso.texto}</Aviso>}

      {d.enRiesgo > 0 && (
        <Aviso tipo="bad">
          {d.enRiesgo} proyecto(s) exceden el presupuesto o van camino a hacerlo.
        </Aviso>
      )}

      {d.conPresupuesto === 0 && (
        <Aviso tipo="info">
          Todavía no hay presupuestos definidos.
          {esDirector
            ? ' Ábre un proyecto y pon cuánto se prevé gastar para poder comparar.'
            : ' La dirección debe definirlos.'}
        </Aviso>
      )}

      <div style={{ display: 'grid', gap: 10 }}>
        {proyectos.map((p) => {
          const e = ESTADO_PRESU[p.estado] || ESTADO_PRESU.sin_presupuesto;
          return (
            <Card key={p.ovId} style={{ padding: 15 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600 }}>
                    {p.ovId} · {p.cliente}
                  </div>
                  <div style={{ fontSize: 11, color: T.muted, marginTop: 2 }}>
                    {p.cantidad} gasto(s) · contrato {fmt.money(p.montoContrato, p.monedaContrato)}
                  </div>
                </div>
                <span style={{
                  fontSize: 10, fontWeight: 700, padding: '3px 9px', borderRadius: 10,
                  background: e.fondo, color: e.color, whiteSpace: 'nowrap', height: 'fit-content',
                }}>
                  {e.texto.toUpperCase()}
                </span>
              </div>

              {p.estado !== 'sin_presupuesto' && (
                <>
                  <div style={{
                    display: 'flex', justifyContent: 'space-between',
                    fontSize: 13, margin: '12px 0 5px',
                  }}>
                    <span>{fmt.money(p.gastado, moneda)} gastado</span>
                    <span style={{ color: T.muted }}>
                      de {fmt.money(p.presupuesto, moneda)}
                    </span>
                  </div>

                  <div style={{
                    position: 'relative', height: 9,
                    background: T.warm, borderRadius: 5, overflow: 'hidden',
                  }}>
                    <div style={{
                      width: `${Math.min(p.consumoPct, 100)}%`, height: '100%',
                      background: e.color, borderRadius: 5,
                    }} />
                    {/* Marca de avance del tiempo: si la barra la pasa,
                        se esta gastando mas rapido de lo que avanza el plazo */}
                    {p.avancePeriodoPct != null && p.avancePeriodoPct < 100 && (
                      <div style={{
                        position: 'absolute', top: -2, bottom: -2,
                        left: `${p.avancePeriodoPct}%`,
                        width: 2, background: T.ink, opacity: 0.5,
                      }} />
                    )}
                  </div>

                  <div style={{ fontSize: 12, color: e.color, marginTop: 8 }}>
                    {p.lectura}
                  </div>

                  {p.proyeccion != null && (
                    <div style={{ fontSize: 11, color: T.muted, marginTop: 4 }}>
                      Proyección al cierre: {fmt.money(p.proyeccion, moneda)}
                      {' · '}la marca vertical es el avance del plazo
                    </div>
                  )}
                </>
              )}

              {esDirector && (
                <div style={{ marginTop: 12 }}>
                  <Boton variante="suave"
                         style={{ fontSize: 12, padding: '5px 11px' }}
                         onClick={() => setEditando(editando === p.ovId ? null : p.ovId)}>
                    {editando === p.ovId ? 'Cerrar' : 'Definir presupuesto'}
                  </Boton>
                </div>
              )}

              {editando === p.ovId && (
                <FormPresupuesto
                  proyecto={p} moneda={moneda}
                  onListo={() => {
                    setEditando(null);
                    setAviso({ tipo: 'ok', texto: 'Presupuesto guardado.' });
                    cargar();
                  }}
                  onError={() => setAviso({ tipo: 'bad', texto: 'No se pudo guardar.' })}
                />
              )}
            </Card>
          );
        })}
      </div>
    </>
  );
}

function FormPresupuesto({ proyecto, moneda, onListo, onError }) {
  const [monto, setMonto] = useState(String(proyecto.presupuesto || ''));
  const [ocupado, setOcupado] = useState(false);

  const guardar = async () => {
    setOcupado(true);
    try {
      await api.guardarPresupuesto(proyecto.ovId, {
        total: Number(monto) || 0,
        moneda,
      });
      onListo();
    } catch {
      onError();
      setOcupado(false);
    }
  };

  const pctContrato = proyecto.montoContrato > 0
    ? ((Number(monto) || 0) / proyecto.montoContrato) * 100 : 0;

  return (
    <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${T.border}` }}>
      <label style={{ fontSize: 11, color: T.muted }}>
        Gasto previsto en {moneda}
        <input inputMode="decimal" value={monto}
               onChange={(e) => setMonto(e.target.value)}
               placeholder="0.00"
               style={{
                 width: '100%', padding: '9px 11px', fontSize: 15, marginTop: 4,
                 border: `1px solid ${T.border}`, borderRadius: 8, boxSizing: 'border-box',
               }} />
      </label>

      {pctContrato > 0 && proyecto.monedaContrato === moneda && (
        <div style={{
          fontSize: 11, marginTop: 6,
          color: pctContrato > 70 ? T.bad : T.muted,
        }}>
          Es el {pctContrato.toFixed(1)}% del contrato
          {pctContrato > 70 && ' — deja poco margen'}
        </div>
      )}

      <Boton onClick={guardar} disabled={ocupado}
             style={{ width: '100%', marginTop: 10 }}>
        {ocupado ? 'Guardando…' : 'Guardar'}
      </Boton>
    </div>
  );
}

// ── Piezas compartidas ──────────────────────────────────────────────
function Bloque({ titulo, children }) {
  return (
    <div>
      <div style={{
        fontSize: 10, color: T.muted, fontWeight: 700,
        letterSpacing: '.05em', textTransform: 'uppercase', marginBottom: 7,
      }}>
        {titulo}
      </div>
      <div style={{ display: 'grid', gap: 7 }}>{children}</div>
    </div>
  );
}

function Fila({ izquierda, derecha, detalle, color }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontSize: 12, color: T.text }}>{izquierda}</div>
        {detalle && (
          <div style={{ fontSize: 11, color: T.muted, marginTop: 1 }}>{detalle}</div>
        )}
      </div>
      <div style={{
        fontSize: 13, fontWeight: 600,
        color: color || T.navy, whiteSpace: 'nowrap',
      }}>
        {derecha}
      </div>
    </div>
  );
}
